import React, { type ReactNode } from "react";
import { type GetStaticProps, type NextPage } from "next";
import Image from "next/image";
import {
  type Banner,
  type Customer,
  type IntegrationSimple,
  type Page as SanityPage,
  getAnnouncementBanner,
  getPage,
  getSimpleIntegrations,
  getStackedLogos,
} from "lib/sanity";
import { AspectRatio, Flex, SimpleGrid } from "@chakra-ui/react";
import { getPageMetaImage } from "utils";
import { Page } from "components/page";
import { HalfHeroAnimation } from "components/half-hero-animation";
import {
  CopilotAnimation,
  HeroAnimation,
  SyncFunnelsAnimation,
  AnalyzeFunnelsAnimation,
  BreakdownsAnimation,
} from "components/platform/campaign-intelligence/animations";
import { StackedLogos } from "components/logos";
import { Section, sectionSpacingY } from "components/section";
import { ProductPair } from "components/product-pair";
import { SplitsAnimation } from "components/animations";
import { ComposableCdpDiagram } from "components/composable-cdp-diagram";
import DestinationMarquee from "components/destination-marquee";
import { SectionHeading } from "components/section-heading";
import { RadialGradient } from "ui/radial-gradient";
import { GradientCard } from "ui/cards/gradient-card";
import { useHeaderHeight } from "contexts/header-height";
import { SecurityFeatures } from "components/content";
import { Rule } from "ui/rule";
import { horizontalContainerPadding } from "ui/container";
import firstFeatureImage from "../../../public/assets/platform/campaign-intelligence/feature-1.png";
import secondFeatureImage from "../../../public/assets/platform/campaign-intelligence/feature-2.png";
import splitsMobile from "../../../public/assets/animations/splits-mobile.png";
import breakdownsMobile from "../../../public/assets/platform/campaign-intelligence/breakdowns-mobile.png";
import analyzeFunnelsMobile from "../../../public/assets/platform/campaign-intelligence/analyze-funnels-mobile.png";

const PRODUCT_PAIR_GAP = { base: 12, lg: 16, xl: 24 };

function ImageContainer({ children }: { children: ReactNode }) {
  return (
    <GradientCard
      variant="blue"
      angle="20deg"
      startColorStop="-80%"
      endColorStop="140%"
      display="flex"
      flexDir="column"
      gap={{ base: 10, xl: 14 }}
      pt={{ base: 10, xl: 14 }}
      pb={0}
      overflow="hidden"
      width="100%"
    >
      {children}
    </GradientCard>
  );
}

type PageProps = {
  page: SanityPage;
  announcementBanner?: Banner;
  destinations: IntegrationSimple[];
  customerRows: Customer[][];
};

const CampaignIntelligence: NextPage<Readonly<PageProps>> = ({
  page,
  announcementBanner,
  destinations,
  customerRows,
}) => {
  const { headerHeight } = useHeaderHeight();

  return (
    <Page
      hasSmoothScroll
      title={page.metaTitle}
      description={page.metaDescription}
      image={getPageMetaImage({ src: page.metaImageUrl, alt: page.metaTitle })}
      announcementBanner={announcementBanner}
    >
      <HalfHeroAnimation
        hideSecondary
        eyebrow={page.eyebrow}
        title={page.title}
        description={page.description}
        gap={{ base: 12, md: 16, lg: 6, xl: 16 }}
        animationElement={<HeroAnimation />}
        backgroundGradient={
          <>
            <RadialGradient
              variant="mint"
              bottom={{ base: "72%", lg: "88%" }}
              right={{ base: "70%", lg: "40%" }}
              boxSize={{ base: "70%", lg: "80%" }}
              opacity={{ base: 0.2, xl: 0.6 }}
            />
            <RadialGradient
              variant="mint"
              bottom={{ base: "20%", lg: "0%" }}
              right={{ base: "-50%", lg: "-74%" }}
              w={{ base: "90%", lg: "120%" }}
              h={{ base: "50%", md: "60%", lg: "200%" }}
              opacity={{ base: 0.4, sm: 0.8, xl: 1 }}
            />
            <RadialGradient
              variant="purple"
              top={{ base: "42%", md: "34%", lg: "-20%" }}
              right={{ base: "28%", md: "18%", lg: "20%" }}
              boxSize={{ base: "45%", lg: "40%" }}
              opacity={0.25}
            />
          </>
        }
      />

      <StackedLogos title="" customerRows={customerRows} my={{ base: 8, md: 0 }} />

      <Section center>
        <SectionHeading
          eyebrow="Warehouse-native campaign intelligence"
          title="Make better decisions with all of your data"
          maxW="3xl"
          mx="auto"
        />

        <GradientCard
          variant="green"
          mt={{ base: 12, sm: 16, lg: 20 }}
          pt={{ base: 10, md: 12, lg: "60px" }}
          px={{ base: 3, sm: 6, md: 12, lg: "60px" }}
          overflow="hidden"
          width="100%"
        >
          <SectionHeading
            textStyle="md"
            titleElement="h3"
            title={"Discover customer behaviors\n and take action"}
            description="Explore customer data to find new opportunities. Put insights to work by creating and syncing audiences to your tools."
            maxW="xl"
            mx="auto"
          />

          <SyncFunnelsAnimation mt={{ base: 12, lg: 14 }} maxW="4xl" mx="auto" />
        </GradientCard>

        <SimpleGrid columns={{ base: 1, md: 2 }} gap={{ base: 7, xl: 10 }} mt={{ base: 7, lg: 10 }} w="100%">
          <ImageContainer>
            <SectionHeading
              textStyle="md"
              titleElement="h3"
              title={"Optimize campaigns \nacross channels"}
              description="Compare campaigns apples-to-apples.  Explore data across your business, including  spend, engagement, and conversions."
              px={{ base: 6, lg: 10, xl: 12 }}
            />

            <AspectRatio
              ratio={540 / 270}
              w="100%"
              maxW={{ base: "88%", lg: "93%" }}
              ml="auto"
              mt="auto"
              pos="relative"
              boxShadow="base"
            >
              <Image fill src={firstFeatureImage} alt="Optimize campaigns across channels" sizes="100%" />
            </AspectRatio>
          </ImageContainer>

          <ImageContainer>
            <SectionHeading
              textStyle="md"
              titleElement="h3"
              title={"Run A/B experiments \nand incrementality tests"}
              description="Measure results across audience cohorts, no matter what channels you test or where your customers convert."
              px={{ base: 6, lg: 9, xl: 12 }}
            />

            <AspectRatio ratio={540 / 270} w="100%" maxW="92%" ml="auto" mt="auto" pos="relative" boxShadow="base">
              <Image fill src={secondFeatureImage} alt="" sizes="100%" />
            </AspectRatio>
          </ImageContainer>
        </SimpleGrid>
      </Section>

      <Section id="copilot" scrollMarginTop={`${headerHeight + 32}px`}>
        <CopilotAnimation />
      </Section>

      <Section>
        <SectionHeading
          eyebrow="Campaign Intelligence features"
          title="You can do more when intelligence is built into your CDP"
          description="You build audiences for campaigns in your CDP. You should be able to explore complete customer profiles and analyze campaign results in it, too."
          maxW="4xl"
          mx="auto"
        />

        <Flex direction="column" gap={{ base: 20, lg: 24, xl: 28 }} mt={{ base: 20, lg: 24, xl: 28 }} w="100%">
          <ProductPair
            align="right"
            eyebrow="Performance"
            title="Measure trends over time"
            description="Define and track all metrics across any audience, campaign, or A/B test to understand how your marketing activities are driving growth for your business."
            mobileImage={splitsMobile}
            animationElement={<SplitsAnimation />}
            animationProps={{ minHeight: { base: "320px", lg: "390px", xl: "470px" } }}
            gap={PRODUCT_PAIR_GAP}
          />

          <ProductPair
            align="left"
            eyebrow="Breakdowns"
            title="Slice data by any attribute"
            theme="blue"
            description="Drill down into events, audiences, and metrics by any attribute to uncover new learnings from your full data."
            mobileImage={breakdownsMobile}
            animationElement={<BreakdownsAnimation />}
            animationProps={{ minHeight: { base: "288px", md: "420px", lg: "445px" } }}
          />

          <ProductPair
            align="right"
            eyebrow="Funnels"
            title="Analyze customer journeys"
            description="Build custom funnels to discover unique patterns in your customer journeys to understand cross-channel behavior and find new growth opportunities."
            mobileImage={analyzeFunnelsMobile}
            animationElement={<AnalyzeFunnelsAnimation />}
            animationProps={{ minHeight: { base: "288px", lg: "350px", xl: "425px" } }}
            gap={PRODUCT_PAIR_GAP}
          />
        </Flex>
      </Section>

      <ComposableCdpDiagram
        variant="campaign-intelligence"
        sectionTitle="Power more effective marketing with a Composable CDP"
      />

      <SecurityFeatures />

      <Rule maxW="container.section" mx="auto" my={{ base: "biggest", md: "huger" }} px={horizontalContainerPadding} />

      <DestinationMarquee destinations={destinations} my="0" pb={sectionSpacingY} />
    </Page>
  );
};

export default CampaignIntelligence;

export const getStaticProps: GetStaticProps<PageProps> = async () => {
  const [page, announcementBanner, destinations, customerRows] = await Promise.all([
    getPage("/platform/campaign-intelligence"),
    getAnnouncementBanner(),
    getSimpleIntegrations("destination"),
    getStackedLogos(),
  ]);

  if (!page) {
    return {
      notFound: true,
    };
  }

  return {
    props: {
      page,
      announcementBanner,
      destinations,
      customerRows,
    },
    revalidate: 60,
  };
};
